import React, { useMemo, useState, useEffect } from 'react';
import {
  ReactTabsFunctionComponent,
  TabListProps,
  TabPanelProps,
  Tabs as ReactTabs,
} from 'react-tabs';
import styled, { useTheme } from 'styled-components';

import { LightTheme, DarkTheme } from 'src/packages/theme';

import { STab, STabListWrapper, STabList, STabPanel, RightElementWrapper } from './Tabs.styles';
import { TabsProps } from './Tabs.types';

const TabsWrapper = styled.div`
  display: flex;
  width: 100%;
  direction: ltr;
  flex-direction: ${(props) => (props.theme.rtl ? 'row-reverse' : 'row')};
`;

const Tabs: React.FC<TabsProps> = (props: TabsProps) => {
  const {
    tabsList,
    tabsChildern,
    borderBottom = 'none',
    style = {},
    showTabsList = true,
    rightElement = null,
    theme,
    ...rest
  } = props;

  const isControlled = 'selectedIndex' in rest;
  const contextTheme = useTheme();
  const [internalSelectedIndex, setInternalSelectedIndex] = useState(0);

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  // required for custom tabs from react-tabs
  (STab as any as ReactTabsFunctionComponent<TabsProps>).tabsRole = 'Tab';
  (STabList as any as ReactTabsFunctionComponent<TabListProps>).tabsRole = 'TabList';
  (STabPanel as any as ReactTabsFunctionComponent<TabPanelProps>).tabsRole = 'TabPanel';

  useEffect(() => {
    if (!isControlled && internalSelectedIndex >= tabsChildern.length) {
      setInternalSelectedIndex(0);
    }
  }, [tabsChildern.length, internalSelectedIndex, isControlled]);

  const handleSelect = (index: number, lastIndex: number, event: Event) => {
    if (!isControlled) {
      setInternalSelectedIndex(index);
    }
    rest.onSelect?.(index, lastIndex, event);
  };

  return (
    <TabsWrapper>
      <ReactTabs
        selectedIndex={isControlled ? rest.selectedIndex : internalSelectedIndex}
        onSelect={handleSelect}
        selectedTabClassName='is-selected'
        selectedTabPanelClassName='is-selected'
        style={{ ...style, width: '100%' }}
        {...rest}
      >
        <STabListWrapper borderBottom={borderBottom} showTabsList={showTabsList}>
          <STabList>
            {tabsList.map((tab, index) => (
              <STab key={index} defaulttheme={defaultTheme}>
                {tab}
              </STab>
            ))}
          </STabList>

          {rightElement && <RightElementWrapper>{rightElement}</RightElementWrapper>}
        </STabListWrapper>

        {tabsChildern.map((child, index) => (
          <STabPanel key={index}>{child}</STabPanel>
        ))}
      </ReactTabs>
    </TabsWrapper>
  );
};

export default Tabs;
